import { m } from '#/paraglide/messages';
import { Radio, RadioGroup } from 'react-aria-components';
import { cn } from 'tailwind-variants';
import UmbSpriteIcon from '#/_packages/umbrella/features/icons/components/umb-sprite-icon';

export type RatingProps = {
    label: string;
    value?: number;
    max?: number;
    descriptions?: readonly string[];
    onChange?: (value: number) => void;
    className?: string;
};

function StarIcon({ filled }: { filled: boolean }) {
    return (
        <UmbSpriteIcon
            icon={filled ? 'lucide-star-filled' : 'lucide-star'}
            iconSize='small'
            className='pointer-events-none'
            // Center the Lucide star's painted bounds inside the hover circle.
            style={{ transform: 'translateY(1.5%)' }}
        />
    );
}

export function Rating({
    label,
    value = 0,
    max = 5,
    descriptions,
    onChange,
    className,
}: RatingProps) {
    const resolvedDescriptions = descriptions ?? [
        m.rating_very_dissatisfied(),
        m.rating_somewhat_dissatisfied(),
        m.rating_neither_satisfied_nor_dissatisfied(),
        m.rating_somewhat_satisfied(),
        m.rating_very_satisfied(),
    ];
    const options = Array.from({ length: max }, (_, index) => index + 1);

    return (
        <div
            className={cn(
                'rating-row flex w-full items-center justify-between gap-2 border-t border-border-base py-2',
                className,
            )}
        >
            <span>{label}</span>
            <RadioGroup
                aria-label={label}
                className='rating flex shrink-0'
                orientation='horizontal'
                value={value > 0 ? String(value) : null}
                onChange={(nextValue) => onChange?.(Number(nextValue))}
            >
                {options.map((ratingValue, index) => {
                    const description =
                        resolvedDescriptions[index] ??
                        m.rating_value({ rating: ratingValue });
                    return (
                        <Radio
                            key={ratingValue}
                            value={String(ratingValue)}
                            className={cn(
                                'inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-transparent bg-transparent p-1 font-bold transition data-[hovered]:bg-(--umb-light-translucent) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                                ratingValue <= value
                                    ? 'text-amber-500'
                                    : 'text-text-subtle hover:text-text-muted',
                            )}
                            data-rating={ratingValue}
                        >
                            <StarIcon filled={ratingValue <= value} />
                            <span className='sr-only'>{description}</span>
                        </Radio>
                    );
                })}
            </RadioGroup>
        </div>
    );
}
